/**
 * ConversionKit — 4 features conversion en 1 composant
 * 1. StickyMobileCTA : bandeau fixe bas de mobile (Appel + Devis + WhatsApp)
 * 2. ExitIntentPopup : popup quand souris va fermer l'onglet (redirect vers /contact ou /tarifs)
 * 3. SocialProofBanner : bandeau haut cliquable "60+ avis 4.9★"
 * 4. WhatsAppFloat : bouton WhatsApp flottant avec message contextuel selon la page
 *
 * Zero email, zero JS bloquant, respect RGPD (aucun tracking direct), zero layout shift.
 */
import { useEffect, useState, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Phone, MessageCircle, Star, X, ChevronRight, Mail } from 'lucide-react';

const PHONE = '+33635512582';
const PHONE_DISPLAY = '06 35 51 25 82';
const WHATSAPP_NUMBER = '33635512582';

// Messages WhatsApp pre-rédigés par contexte
const WHATSAPP_MESSAGES = {
  '/mascottes': 'Bonjour ! Je souhaite un devis pour une mascotte. Merci !',
  '/anniversaire': 'Bonjour ! Je souhaite un devis pour un anniversaire enfant. Merci !',
  '/spectacles': 'Bonjour ! Je souhaite un devis pour un spectacle. Merci !',
  '/location': 'Bonjour ! Je souhaite un devis pour la location d\'une machine. Merci !',
  '/location-glace-italienne': 'Bonjour ! Je souhaite un devis pour la location d\'une machine à glace italienne. Merci !',
  '/loustic-breizh': 'Bonjour ! Je souhaite un devis pour le food truck Loustic Breizh. Merci !',
  '/arbre-de-noel-entreprise': 'Bonjour ! Je souhaite un devis pour un arbre de Noël CSE / entreprise. Merci !',
  '/pere-noel-entreprise-bretagne': 'Bonjour ! Je souhaite un devis pour un Père Noël pro. Merci !',
};

const getContextualMessage = (pathname) => {
  // Match exact d'abord
  if (WHATSAPP_MESSAGES[pathname]) return WHATSAPP_MESSAGES[pathname];
  // Match par mots-cles dans le path
  if (pathname.includes('mascotte')) return WHATSAPP_MESSAGES['/mascottes'];
  if (pathname.includes('anniversaire')) return WHATSAPP_MESSAGES['/anniversaire'];
  if (pathname.includes('spectacle') || pathname.includes('magicien')) return WHATSAPP_MESSAGES['/spectacles'];
  if (pathname.includes('food-truck') || pathname.includes('hot-dog') || pathname.includes('lichou')) return WHATSAPP_MESSAGES['/loustic-breizh'];
  if (pathname.includes('glace-italienne')) return WHATSAPP_MESSAGES['/location-glace-italienne'];
  if (pathname.includes('location')) return WHATSAPP_MESSAGES['/location'];
  if (pathname.includes('arbre-de-noel') || pathname.includes('arbre-noel')) return WHATSAPP_MESSAGES['/arbre-de-noel-entreprise'];
  if (pathname.includes('pere-noel') || pathname.includes('père-noël')) return WHATSAPP_MESSAGES['/pere-noel-entreprise-bretagne'];
  return 'Bonjour ! Je souhaite un devis pour une animation. Merci !';
};

// Chemins où on masque tous les widgets (admin, checkout, etc.)
const HIDDEN_PATHS = ['/admin', '/contact'];
const shouldHide = (pathname) => HIDDEN_PATHS.some((p) => pathname.startsWith(p));

// ---------------------------------------------------------------------------
// 1. Sticky Mobile CTA (bandeau bas fixe, mobile only)
// ---------------------------------------------------------------------------
export function StickyMobileCTA() {
  const { pathname } = useLocation();
  if (shouldHide(pathname)) return null;
  const waMessage = encodeURIComponent(getContextualMessage(pathname));
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white/95 backdrop-blur-md border-t border-ink/10 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-2 py-2 flex gap-2"
      style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
      data-testid="sticky-mobile-cta"
    >
      <a
        href={`tel:${PHONE}`}
        className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-full py-2.5 text-sm shadow-sm transition-colors"
        data-testid="sticky-mobile-call"
      >
        <Phone className="w-4 h-4" />
        Appeler
      </a>
      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waMessage}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE57] text-white font-semibold rounded-full py-2.5 text-sm shadow-sm transition-colors"
        data-testid="sticky-mobile-whatsapp"
      >
        <MessageCircle className="w-4 h-4" />
        WhatsApp
      </a>
      <Link
        to="/contact"
        className="flex-1 flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-full py-2.5 text-sm shadow-sm transition-colors"
        data-testid="sticky-mobile-devis"
      >
        <Mail className="w-4 h-4" />
        Devis
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. Exit Intent Popup (desktop only, 1x par session)
// ---------------------------------------------------------------------------
export function ExitIntentPopup() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    if (shouldHide(pathname)) return;
    // Une seule fois par session
    if (sessionStorage.getItem('exitIntentShown') === '1') {
      setTriggered(true);
      return;
    }
    // Delai anti-fausse-alerte : n'active qu'apres 8s de lecture
    const activateT = setTimeout(() => setTriggered(true), 8000);
    return () => clearTimeout(activateT);
  }, [pathname]);

  useEffect(() => {
    if (!triggered || sessionStorage.getItem('exitIntentShown') === '1') return;
    const onMouseLeave = (e) => {
      // Detection : souris sort par le haut du viewport (vers l'onglet)
      if (e.clientY <= 0 && !open) {
        setOpen(true);
        sessionStorage.setItem('exitIntentShown', '1');
      }
    };
    document.addEventListener('mouseleave', onMouseLeave);
    return () => document.removeEventListener('mouseleave', onMouseLeave);
  }, [triggered, open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={() => setOpen(false)}
      data-testid="exit-intent-overlay"
    >
      <div
        className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="exit-intent-modal"
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-ink/5 transition-colors"
          aria-label="Fermer"
          data-testid="exit-intent-close"
        >
          <X className="w-5 h-5 text-ink-muted" />
        </button>
        <div className="text-center mb-5">
          <div className="text-4xl mb-2">🎪</div>
          <h3 className="font-heading text-2xl font-bold text-ink mb-2">
            Attendez ! Avant de partir…
          </h3>
          <p className="text-sm text-ink-muted leading-relaxed">
            Découvrez notre <strong>grille tarifaire complète</strong> : anniversaires dès 110€, spectacles dès 850€, locations machines dès 60€. Ou obtenez un <strong>devis gratuit en 24h</strong>.
          </p>
        </div>
        <div className="space-y-2.5">
          <Link
            to="/tarifs"
            onClick={() => setOpen(false)}
            className="flex items-center justify-center gap-2 w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-full py-3 shadow-sm transition-colors"
            data-testid="exit-intent-tarifs"
          >
            Voir la grille tarifaire complète
            <ChevronRight className="w-4 h-4" />
          </Link>
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            className="flex items-center justify-center gap-2 w-full bg-white hover:bg-ink/5 border-2 border-ink/10 text-ink font-semibold rounded-full py-3 transition-colors"
            data-testid="exit-intent-devis"
          >
            <Mail className="w-4 h-4" />
            Obtenir un devis en 24h
          </Link>
          <a
            href={`tel:${PHONE}`}
            className="flex items-center justify-center gap-2 w-full text-emerald-700 hover:bg-emerald-50 font-semibold rounded-full py-2.5 transition-colors text-sm"
            data-testid="exit-intent-phone"
          >
            <Phone className="w-4 h-4" />
            Ou appelez-nous : {PHONE_DISPLAY}
          </a>
        </div>
        <p className="text-center text-xs text-ink-muted mt-4">
          ⭐ 4,9/5 · 60+ avis Google · 15 ans en Bretagne
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Social Proof Banner (top de page, discret, cliquable Google)
// ---------------------------------------------------------------------------
export function SocialProofBanner() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (sessionStorage.getItem('socialProofDismissed') === '1') setVisible(false);
  }, []);
  if (!visible || shouldHide(pathname)) return null;
  return (
    <div
      className="relative bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-300 text-ink text-xs sm:text-sm font-medium px-3 py-2 text-center border-b border-amber-500/30"
      data-testid="social-proof-banner"
    >
      <a
        href="https://www.google.com/search?sca_esv=&q=Le+Clown+Magicien+Quimper#lrd=0x0:0x0,1"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 hover:underline"
        data-testid="social-proof-link"
      >
        <Star className="w-3.5 h-3.5 fill-current text-amber-700" />
        <strong>4,9/5</strong>
        <span className="hidden sm:inline">sur Google</span>
        <span>·</span>
        <strong>60+ avis</strong>
        <span className="hidden sm:inline">de vrais clients</span>
        <span>·</span>
        <span>15 ans en Bretagne</span>
      </a>
      <button
        onClick={() => {
          setVisible(false);
          sessionStorage.setItem('socialProofDismissed', '1');
        }}
        className="absolute top-1/2 right-2 -translate-y-1/2 p-0.5 opacity-60 hover:opacity-100"
        aria-label="Fermer"
        data-testid="social-proof-close"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4. Floating WhatsApp button (desktop + tablet, contextuel par page)
// ---------------------------------------------------------------------------
export function WhatsAppFloat() {
  const { pathname } = useLocation();
  if (shouldHide(pathname)) return null;
  const message = encodeURIComponent(getContextualMessage(pathname));
  return (
    <a
      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`}
      target="_blank"
      rel="noopener noreferrer"
      className="hidden lg:flex fixed bottom-6 right-6 z-40 w-14 h-14 bg-[#25D366] hover:bg-[#1EBE57] text-white rounded-full items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-105"
      aria-label="Contacter par WhatsApp"
      data-testid="whatsapp-float"
    >
      <MessageCircle className="w-6 h-6" />
    </a>
  );
}

// ---------------------------------------------------------------------------
// Composant wrapper qui active les 4 features
// ---------------------------------------------------------------------------
export function ConversionKit() {
  return (
    <>
      <SocialProofBanner />
      <StickyMobileCTA />
      <ExitIntentPopup />
      <WhatsAppFloat />
    </>
  );
}
